import { Injectable } from '@angular/core';
import { ApplicationFormStates, ITerminalTheme } from '@boldpenguin/sdk';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SelectorWrapperService } from './selector-wrapper.service';

// TODO: Latest SDK is not compatible until https://github.com/BoldPenguin/emperor/pull/977
interface ITerminalThemeWithFullLogo extends ITerminalTheme {
  logo_img_url_full?: File;
}

@Injectable({
  providedIn: 'root',
})
export class UiService {
  constructor(private selectorWrapperService: SelectorWrapperService) {}

  agencyLogo$ = this.selectorWrapperService
    .getTerminalTheme$()
    .pipe(map(theme => this.themeLogo(theme)));
  agencyLogoFull$ = this.selectorWrapperService.getTerminalTheme$().pipe(
    map(theme => {
      if ((theme as ITerminalThemeWithFullLogo)?.logo_img_url_full) {
        return (theme as ITerminalThemeWithFullLogo)?.logo_img_url_full;
      }
      return 'assets/bp_logo.svg';
    }),
  );

  activeQuestionSetName$ = this.selectorWrapperService
    .getCurrentActiveQuestionSet$()
    .pipe(map(qs => qs?.question_set.name));

  appFormReferenceId$: Observable<string | undefined> =
    this.selectorWrapperService
      .getApplicationForm$()
      .pipe(map(form => form.reference_id));

  formCompleted$: Observable<boolean> = combineLatest({
    isFormLoaded: this.selectorWrapperService.isFormLoaded$(),
    formState: this.selectorWrapperService.getApplicationState$(),
  }).pipe(
    distinctUntilChanged(),
    map(({ isFormLoaded, formState }) => {
      return !isFormLoaded
        ? false
        : formState === ApplicationFormStates.Completed ||
            formState === ApplicationFormStates.Quoted;
    }),
  );

  progressPercent$ = combineLatest({
    progress: this.selectorWrapperService.getApplicationFormProgress$(),
    isComplete: this.formCompleted$,
  }).pipe(map(({ progress, isComplete }) => (isComplete ? 100 : progress)));

  forwardProgressPercent$ = combineLatest({
    progress:
      this.selectorWrapperService.getApplicationFormProgressForwardOnly$(),
    isComplete: this.formCompleted$,
  }).pipe(map(({ progress, isComplete }) => (isComplete ? 100 : progress)));

  stepDescription$ = combineLatest({
    qs: this.selectorWrapperService.getCurrentActiveQuestionSet$(),
    questionSets: this.selectorWrapperService.getQuestionSets$(),
  }).pipe(
    map(({ qs, questionSets }) => {
      if (!!qs?.question_set_id && questionSets?.length) {
        const ids: string[] = questionSets.map(qs => qs.question_set_id);
        return `Step ${ids.indexOf(qs.question_set_id) + 1} of ${ids.length}`;
      }
    }),
  );

  private themeLogo(theme: ITerminalTheme | undefined): string {
    return (theme?.active && theme?.logo_img_url) || 'assets/bp_logo.svg';
  }
}
